/**
 * The patient's own portal URL.
 *
 * Built from the browser's origin, so the link is correct on whichever host the
 * staff member is actually using (dev, LAN, production). Same rule as
 * `buildRegistrationUrl`.
 *
 * The backend has a same-named twin (`src/modules/patient-portal/portal-url.ts`)
 * that takes the configured `PUBLIC_BASE_URL` instead, because the SMS dispatcher
 * has no browser to ask. That one is NOT a duplicate to fold back in: see its
 * header for why the different base is the whole point. Keep the `/p/patient/`
 * path in step between the two — both files pin it in a test.
 *
 * No `?lang=` parameter, unlike the registration link. That one is printed as
 * three posters and scanned by a stranger, so the language has to travel with
 * the paper; this one goes to a single person who can use the toggle on the
 * page. A parameter nobody sets is a parameter that rots.
 */
/**
 * The SPA path the portal is served at, without the token.
 *
 * Exported and consumed by `App.tsx`'s `<Route>` so the router and the link
 * builder cannot disagree. They previously held the literal separately, which
 * meant the two "pin" tests (this stack's and the backend's) each asserted a
 * builder against itself: change the route and both stayed green while every
 * link 404'd.
 */
export const PATIENT_PORTAL_PATH = "/p/patient";

/** The router pattern, so the `<Route>` element derives from the same constant. */
export const PATIENT_PORTAL_ROUTE = `${PATIENT_PORTAL_PATH}/:portalToken`;

export function buildPatientPortalUrl(origin: string, portalToken: string): string {
  return new URL(`${PATIENT_PORTAL_PATH}/${encodeURIComponent(portalToken)}`, origin).toString();
}

/**
 * Basename for the downloaded QR PNG.
 *
 * Named after the MRN, which is already on the screen the staff member is
 * looking at. Deliberately NOT the token — a QR sitting in a shared Downloads
 * folder should not have the credential spelled out in its filename — and
 * deliberately not the patient's name, so the filename is not PHI either.
 */
export function patientPortalQrFileBase(mrn: string): string {
  return `patient-${mrn}`;
}
